import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function RSVP() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    participation: '' // 'yes' or 'no'
  });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Supabase URL'lerini al
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  // Dil seçenekleri
  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    // Kullanıcı bağlantısını logla
    logUserConnection();
  }, []);

  const logUserConnection = async () => {
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/connection-logger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          user_agent: navigator.userAgent,
          page_url: window.location.href
        }),
      });
    } catch (error) {
      console.error('Bağlantı logu kaydedilemedi:', error);
    }
  };

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setShowLanguageDropdown(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleParticipationChoice = (choice: 'yes' | 'no') => {
    setFormData(prev => ({
      ...prev,
      participation: choice
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName.trim()) {
      alert(t('name_required'));
      return;
    }

    if (!formData.participation) {
      alert(t('participation_required'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/rsvp-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          full_name: formData.fullName.trim(),
          participation: formData.participation === 'yes'
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        // Formu temizle
        setFormData({
          fullName: '',
          participation: ''
        });
      } else {
        throw new Error(result.error || 'Kayıt sırasında bir hata oluştu.');
      }

    } catch (error) {
      console.error('Registration error:', error);
      setSubmitStatus('error');
      alert(error instanceof Error ? error.message : t('registration_error_message'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/8cb33656b5a0f0232dfd58e6d65f1d2d.png)'}}>
      {/* Overlay for better readability */}
      <div className="min-h-screen bg-black/20">
        {/* Header */}
        <header className="bg-white/90 backdrop-blur-sm shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              {/* Logo - Left Aligned */}
              <div className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/f88f2b000305bceb9f03b3f9ed05ac3d.png" 
                  alt="Logo" 
                  className="w-12 h-12 object-contain"
                />
              </div>

              {/* Title - Centered */}
              <div className="absolute left-1/2 transform -translate-x-1/2 text-center">
                <h1 className="text-2xl font-bold text-gray-900 font-serif">{t('site_title')}</h1>
                <p className="text-gray-600 text-sm">{t('site_subtitle')}</p>
              </div>

              {/* Language Selector - Right */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 bg-white/80 hover:bg-white border border-gray-200 rounded-lg transition-colors cursor-pointer"
                >
                  <span className="text-lg">{currentLanguage.flag}</span>
                  <span className="text-sm font-medium text-gray-700">{currentLanguage.name}</span>
                  <i className="ri-arrow-down-s-line text-gray-500"></i>
                </button>

                {/* Language Dropdown */}
                {showLanguageDropdown && (
                  <div className="absolute top-full right-0 z-50 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => handleLanguageChange(language.code)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 text-left cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          currentLanguage.code === language.code ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{language.flag}</span>
                        <span className="text-sm font-medium">{language.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8">
            {/* Welcome Message */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-transparent rounded-full flex items-center justify-center mx-auto mb-4">
                <img 
                  src="https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/f88f2b000305bceb9f03b3f9ed05ac3d.png" 
                  alt="Logo" 
                  className="w-14 h-14 object-contain"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('event_registration_form')}</h2>
              <p className="text-gray-600">{t('form_description')}</p>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                  <div>
                    <h3 className="text-green-800 font-semibold">{t('registration_success')}</h3>
                    <p className="text-green-700 text-sm">{t('registration_success_message')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                  <div>
                    <h3 className="text-red-800 font-semibold">{t('registration_error')}</h3>
                    <p className="text-red-700 text-sm">{t('registration_error_message')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Simplified Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name Field */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('full_name')}
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                  placeholder={t('full_name_placeholder')}
                />
              </div>

              {/* Participation Choice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('participation_question')}
                </label>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleParticipationChoice('yes')}
                    className={`w-full py-4 px-6 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                      formData.participation === 'yes'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-3">
                      <i className="ri-check-line text-xl"></i>
                      <span className="font-medium">{t('yes_participate')}</span>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleParticipationChoice('no')}
                    className={`w-full py-4 px-6 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                      formData.participation === 'no'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-3">
                      <i className="ri-close-circle-line text-xl"></i>
                      <span className="font-medium">{t('no_participate')}</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !formData.fullName.trim() || !formData.participation}
                className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t('submitting')}
                  </span>
                ) : (
                  t('confirm_rsvp')
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-white">
            <p>{t('copyright')}</p>
            <p className="mt-1">
              <a href="https://readdy.ai/?origin=logo" className="text-white/80 hover:text-white transition-colors">
                {t('powered_by')}
              </a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
